import { Injectable, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, tap, firstValueFrom } from 'rxjs';
import { environment } from '../../../environments/environment';

export interface User {
  id: number;
  nome: string;
  email: string;
  tipo: 'paciente' | 'cliente' | 'farmacia' | 'clinica' | 'admin' | 'deposito' | 'profissional_saude';
  telefone?: string | null;
  endereco?: string | null;
  cidade?: string | null;
  foto?: string | null;
  farmaciaId?: number;
  farmacia?: {
    id: number;
    nome: string;
    foto?: string | null;
    fotoCapa?: string | null;
  } | null;
  clinicaId?: number;
  clinica?: {
    id: number;
    nome: string;
    foto?: string | null;
  } | null;
  depositoId?: number;
  deposito?: {
    id: number;
    nome: string;
    foto?: string | null;
  } | null;
}

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private apiUrl = `${environment.apiUrl}/auth`;
  currentUser = signal<User | null>(null);
  private userLoadedPromise: Promise<void> | null = null;

  constructor(
    private http: HttpClient,
    private router: Router
  ) {
    // Carregar usuário do localStorage imediatamente (síncrono)
    const storedUser = localStorage.getItem('currentUser');
    if (storedUser) {
      try {
        const user = JSON.parse(storedUser);
        this.currentUser.set(user);
     //   console.log('[AuthService] User loaded from localStorage (sync):', user);
      } catch (e) {
        localStorage.removeItem('currentUser');
      }
    }
    
    // Depois verificar com o servidor (assíncrono)
    this.loadUser();
  }

  register(data: any): Observable<any> {
    return this.http.post(`${this.apiUrl}/register`, data);
  }

  login(email: string, password: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/login`, { email, password }).pipe(
      tap((response: any) => {
        console.log('[AuthService] Login response:', response);
        console.log('[AuthService] User from response:', response.user);
        console.log('[AuthService] User tipo:', response.user?.tipo);
        
        this.currentUser.set(response.user);
        localStorage.setItem('currentUser', JSON.stringify(response.user));
        
        console.log('[AuthService] Current user after set:', this.currentUser());
        console.log('[AuthService] User tipo after set:', this.currentUser()?.tipo);
      })
    );
  }

  logout(): Observable<any> {
    return this.http.post(`${this.apiUrl}/logout`, {}).pipe(
      tap(() => {
        this.currentUser.set(null);
        localStorage.removeItem('currentUser');
        this.router.navigate(['/login']);
      })
    );
  }

  async loadUser(): Promise<void> {
    if (this.userLoadedPromise) {
      return this.userLoadedPromise;
    }

    this.userLoadedPromise = firstValueFrom(
      this.http.get<{ user: User }>(`${this.apiUrl}/me`)
    ).then(
      (response) => {
        if (response.user) {
          this.currentUser.set(response.user);
          localStorage.setItem('currentUser', JSON.stringify(response.user));
         /* console.log('[AuthService] User loaded from server:', response.user);
          console.log('[AuthService] User tipo:', response.user.tipo);*/
        } else {
          this.currentUser.set(null);
          localStorage.removeItem('currentUser');
          console.log('[AuthService] No user returned from server');
        }
      },
      (error) => {
        console.error('[AuthService] Failed to load user from server:', error);
        console.error('[AuthService] Error status:', error.status);
        // Se for 401, limpar dados locais
        if (error.status === 401) {
          this.currentUser.set(null);
          localStorage.removeItem('currentUser');
          console.log('[AuthService] Cleared local user data due to 401');
        }
      }
    );

    return this.userLoadedPromise;
  }

  isAuthenticated(): boolean {
    return this.currentUser() !== null;
  }

  isFarmacia(): boolean {
    return this.currentUser()?.tipo === 'farmacia';
  }

  // Atualizar dados do usuário do servidor
  async refreshUser(): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<{ user: User }>(`${this.apiUrl}/me`)
      );
      
      if (response.user) {
        this.currentUser.set(response.user);
        localStorage.setItem('currentUser', JSON.stringify(response.user));
        console.log('[AuthService] User refreshed:', response.user);
      }
    } catch (error) {
      console.error('[AuthService] Failed to refresh user:', error);
    }
  }
}
